/**
 * API client per la gestione dei pazienti.
 *
 * Endpoints:
 * - GET /api/v1/patients/ - Lista pazienti
 * - GET /api/v1/patients/{id} - Dettaglio paziente
 * - GET /api/v1/patients/{id}/history - Storico completo
 * - POST /api/v1/patients/merge - Merge pazienti duplicati
 */

import axios from 'axios';

export interface PatientItem {
  id: string;
  name: string;
  species: string | null;
  breed: string | null;
  owner_id: string;
  owner_name: string | null;
  cases_count: number;
}

export interface PatientCase {
  id: string;
  title: string | null;
  specialty: string | null;
  status: string | null;
  priority: string | null;
  opened_at: string | null;
  closed_at: string | null;
}

export interface PatientDetail {
  id: string;
  name: string;
  species: string | null;
  breed: string | null;
  birth_date: string | null;
  gender: string | null;
  notes: string | null;
  owner_id: string;
  owner_email: string | null;
  owner_name: string | null;
  owner_phone: string | null;
  cases: PatientCase[];
}

export interface HistoryEmail {
  id: string;
  subject: string | null;
  from_email: string;
  category: string | null;
  priority: string | null;
  received_at: string | null;
  summary_ai: string | null;
}

export interface HistoryThread {
  id: string;
  subject: string | null;
  first_received_at: string | null;
  emails: HistoryEmail[];
}

export interface HistoryCase {
  id: string;
  title: string | null;
  specialty: string | null;
  status: string | null;
  priority: string | null;
  opened_at: string | null;
  closed_at: string | null;
  threads: HistoryThread[];
}

export interface PatientInfo {
  id: string;
  name: string;
  species: string | null;
  breed: string | null;
  birth_date: string | null;
  gender: string | null;
}

export interface OwnerInfo {
  id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
}

export interface PatientHistory {
  patient: PatientInfo;
  owner: OwnerInfo | null;
  cases: HistoryCase[];
  total_emails: number;
}

export interface MergePatientsRequest {
  source_patient_id: string;
  target_patient_id: string;
}

export interface MergeResponse {
  message: string;
  source_patient_id: string;
  target_patient_id: string;
  transferred_cases: number;
}

const api = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export interface PatientCreate {
  name: string;
  species?: string;
  breed?: string;
  birth_date?: string;
  gender?: string;
  notes?: string;
  owner_id: string;
}

export interface PatientUpdate {
  name?: string;
  species?: string;
  breed?: string;
  birth_date?: string;
  gender?: string;
  notes?: string;
  owner_id?: string;
}

export const patientsApi = {
  /**
   * Lista tutti i pazienti.
   */
  list: async (ownerId?: string, search?: string, limit: number = 200, offset: number = 0): Promise<PatientItem[]> => {
    const params: Record<string, string | number> = { limit, offset };
    if (ownerId) params.owner_id = ownerId;
    if (search) params.search = search;
    const response = await api.get<PatientItem[]>('/patients/', { params });
    return response.data;
  },

  /**
   * Ottieni dettaglio paziente.
   */
  get: async (patientId: string): Promise<PatientDetail> => {
    const response = await api.get<PatientDetail>(`/patients/${patientId}`);
    return response.data;
  },

  /**
   * Ottieni storico completo del paziente.
   */
  getHistory: async (patientId: string): Promise<PatientHistory> => {
    const response = await api.get<PatientHistory>(`/patients/${patientId}/history`);
    return response.data;
  },

  /**
   * Merge di due pazienti duplicati.
   */
  merge: async (request: MergePatientsRequest): Promise<MergeResponse> => {
    const response = await api.post<MergeResponse>('/patients/merge', request);
    return response.data;
  },

  /**
   * Crea un nuovo paziente.
   */
  create: async (data: PatientCreate): Promise<PatientDetail> => {
    const response = await api.post<PatientDetail>('/patients/', data);
    return response.data;
  },

  /**
   * Aggiorna un paziente.
   */
  update: async (patientId: string, data: PatientUpdate): Promise<PatientDetail> => {
    const response = await api.patch<PatientDetail>(`/patients/${patientId}`, data);
    return response.data;
  },
};
